import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { authApi } from '@services/api.ts';
import EyeIcon from '@/images/eye.svg?react';
import EyeOffIcon from '@/images/eye-off.svg?react';
import { useAuth } from '@store/hooks/useAuth.ts';

const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { isLoading, error, setLoading, setError, setUser } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [info, setInfo] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const returnUrl = searchParams.get('returnUrl');
  const sessionExpired = searchParams.get('sessionExpired') === 'true';
  const from =
    returnUrl || (location.state as any)?.from?.pathname || '/visualization';

  useEffect(() => {
    if (sessionExpired) {
      setInfo(
        t('auth.sessionExpired') ||
          'Your session has expired. Please login again.'
      );
    }
  }, [sessionExpired, t]);

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (error) {
      setError(null);
    }
    if (info) {
      setInfo(null);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.email.trim()) {
        throw new Error(t('auth.emailRequired'));
      }
      if (!formData.password.trim()) {
        throw new Error(t('auth.passwordRequired'));
      }

      const response = await authApi.login(formData.email, formData.password);

      if (response.data) {
        localStorage.setItem('accessToken', response.data.access);
        localStorage.setItem('refreshToken', response.data.refresh);

        setUser(response.data.user);

        if (formData.rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }

        navigate(from, { replace: true });
      } else {
        throw new Error(t('auth.loginFailed'));
      }
    } catch (err: any) {
      setError(err.message || t('auth.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute top-4 right-4 z-10" dir="ltr">
        <div className="inline-flex gap-1 rounded-lg border border-gray-200 bg-white/80 backdrop-blur-sm p-1 shadow-sm">
          <button
            onClick={() => handleLanguageChange('en')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
              i18n.language === 'en'
                ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            disabled={isLoading}
          >
            English
          </button>
          <button
            onClick={() => handleLanguageChange('ar')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
              i18n.language === 'ar'
                ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            disabled={isLoading}
          >
            العربية
          </button>
        </div>
      </div>

      <div className="max-w-lg w-full space-y-8">
        <div className="backdrop-blur-lg bg-white/70 shadow-2xl rounded-2xl p-8 border border-white/20">
          <div>
            <h2 className="mt-6 text-center text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {t('auth.loginTitle')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('auth.loginSubtitle')}
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            {info && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md break-words">
                {info}
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md break-words">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="label">
                  {t('auth.email')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  required
                  disabled={isLoading}
                  className={`input ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder={t('auth.email')}
                />
              </div>
              <div>
                <label htmlFor="password" className="label">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="current-password"
                    required
                    disabled={isLoading}
                    className={`input ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder={t('auth.password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    className="absolute inset-y-0 end-0 pe-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors disabled:cursor-not-allowed focus:outline-none focus:ring-0 focus:ring-offset-0"
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                />
                <label
                  htmlFor="rememberMe"
                  className="ms-2 block text-sm text-gray-700 cursor-pointer select-none"
                >
                  {t('auth.rememberMe')}
                </label>
              </div>

              <div className="text-sm">
                <a
                  href="#"
                  className="font-medium text-blue-600 hover:text-blue-500 hover:underline transition-colors"
                >
                  {t('auth.forgotPassword')}
                </a>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`btn btn-primary w-full ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isLoading
                  ? t('auth.loggingIn') || 'Logging in...'
                  : t('auth.loginButton')}
              </button>
            </div>

            <div className="text-center">
              <span className="text-sm text-gray-600">
                {t('auth.noAccount')}{' '}
              </span>
              <a
                href="#"
                className="text-sm font-medium text-blue-600 hover:text-blue-500 hover:underline transition-colors"
              >
                {t('auth.register')}
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
